import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./createUser.css";

import { useNavigate } from "react-router-dom";
import { useAuthentication } from "../../../Hooks/useAuthentication";

import useForm from "../../../Hooks/useForm";
import { CreateUserSchema } from "../../../Models/Schemas/CreateUserSchema";
import { CREATE_USER_FORM } from "../../../Constants/createUser";

import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";

import Input from "../../Elements/Input/Input";
import Button from "../../Elements/Button/Button";
import Loader from "../../Elements/Loader/Loader";
import Modal from "../../Elements/Modal";
import { setErrorFormSimpleUtil } from "../../../Utilities/Forms/errors";
import { isAdminRole } from "../../../Constants/roles";

const isFilled = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
};

const CreateUser = () => {
  const navigate = useNavigate();
  const showToast = useToast();

  const [successModal, setSuccessModal] = useState(false);
  const [savedInfo, setSavedInfo] = useState({ userId: null, username: null, createdAt: null });

  const { username: currentUser, isAuthenticated, role } = useAuthentication();

  // ✅ solo admin puede ver/usar esta pantalla
  const onlyAdmin = isAdminRole(role);

  // ✅ store: ajusta el nombre si tu función se llama distinto
  const { fetchDataCreateUser, createUserLoading: loading, createUserError: error } = useStore();

  const { values, errors, touched, handleBlur, handleChange, isValid } = useForm(
    { ...CREATE_USER_FORM },
    CreateUserSchema
  );

  const requiredFields = useMemo(() => ["username", "password", "confirmPassword"], []);

  const allFilled = useMemo(() => {
    return requiredFields.every((k) => isFilled(values?.[k]));
  }, [requiredFields, values]);

  const goHome = () => navigate("/");

  const submit = async () => {
    try {
      // payload: role fijo operador
      const payload = {
        username: String(values.username || "").trim().toLowerCase(),
        password: String(values.password || ""),
        role: "operador",
      };

      const res = await fetchDataCreateUser(payload);

      setSavedInfo({
        userId: res?.data?.user?.userId || null,
        username: res?.data?.user?.username || payload.username,
        createdAt: res?.data?.user?.createdAt || null,
      });

      setSuccessModal(true);
    } catch (e) {
      // el toast lo maneja el effect con "error"
    }
  };

  const handleResponse = useCallback(() => {
    if (error) {
      const message =
        error instanceof Error ? error.message : error?.error || "Error desconocido";
      showToast("error", message);
    }
  }, [error, showToast]);

  useEffect(() => {
    handleResponse();
  }, [handleResponse]);

  // ✅ Bloqueo por auth/rol
  if (!isAuthenticated) {
    return (
      <div className="qs-wrap">
        <div className="container py-4">
          <div className="alert alert-warning mb-0">No has iniciado sesión.</div>
        </div>
      </div>
    );
  }

  if (!onlyAdmin) {
    return (
      <div className="qs-wrap">
        <div className="container py-4">
          <div className="alert alert-danger mb-0">
            No tienes permisos para crear usuarios.
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <Loader />;

  return (
    <>
      <Modal
        display={successModal}
        onClose={setSuccessModal}
        title="✅ Usuario creado"
        size="medium"
        onCloseCallback={goHome}
      >
        <div className="d-flex flex-column gap-2">
          <div className="alert alert-success mb-0">
            El usuario se creó correctamente.
          </div>

          {(savedInfo.userId || savedInfo.username || savedInfo.createdAt) && (
            <div className="small text-muted">
              {savedInfo.userId && (
                <div>
                  <span className="fw-bold">UserId:</span> {savedInfo.userId}
                </div>
              )}
              {savedInfo.username && (
                <div>
                  <span className="fw-bold">Usuario:</span> {savedInfo.username}
                </div>
              )}
              {savedInfo.createdAt && (
                <div>
                  <span className="fw-bold">Fecha:</span> {savedInfo.createdAt}
                </div>
              )}
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <button className="btn btn-success" onClick={goHome}>
              Ir al inicio
            </button>
          </div>
        </div>
      </Modal>

      <div className="qs-wrap">
        <div className="container py-4">
          <div className="card qs-card shadow-sm">
            <div className="card-body p-3 p-md-4">
              {/* Header */}
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 border-bottom pb-3 mb-3">
                <div>
                  <div className="qs-title m-0">Crear Usuario</div>
                  <div className="qs-sub text-muted">Solo administradores</div>
                </div>
                <div className="small text-muted">
                  Admin: <span className="fw-bold text-dark">{currentUser || "Administrador"}</span>
                </div>
              </div>

              {/* Form */}
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <Input
                    label="USUARIO"
                    value={values.username}
                    onChange={handleChange("username")}
                    onBlur={handleBlur("username")}
                    error={setErrorFormSimpleUtil(errors, touched, "username")}
                    success={!setErrorFormSimpleUtil(errors, touched, "username")}
                    autoComplete="off"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <Input
                    type="password"
                    label="CONTRASEÑA"
                    value={values.password}
                    onChange={handleChange("password")}
                    onBlur={handleBlur("password")}
                    error={setErrorFormSimpleUtil(errors, touched, "password")}
                    success={!setErrorFormSimpleUtil(errors, touched, "password")}
                    autoComplete="new-password"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <Input
                    type="password"
                    label="CONFIRMAR CONTRASEÑA"
                    value={values.confirmPassword}
                    onChange={handleChange("confirmPassword")}
                    onBlur={handleBlur("confirmPassword")}
                    error={setErrorFormSimpleUtil(errors, touched, "confirmPassword")}
                    success={!setErrorFormSimpleUtil(errors, touched, "confirmPassword")}
                    autoComplete="new-password"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <Input
                    label="ROL"
                    value="operador"
                    disabled
                  />
                  <div className="small text-muted mt-1">
                    El rol se asigna automáticamente como <b>operador</b>.
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex justify-content-end mt-3 gap-2">
                <Button
                  text="Crear usuario"
                  color="green"
                  fill
                  action={submit}
                  disabled={!isValid || !allFilled}
                />
              </div>

              {!allFilled && (
                <div className="alert alert-info mt-3 mb-0">
                  Completa todos los campos para poder crear el usuario.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateUser;